import { createContext, startTransition, useRef, useState } from "react";

import { getHaNSearchKinds, type HaNSearchPerson, searchesHaNPeople } from "@lib/logic";
import { clearPeopleSearch, searchPeople, useAppDispatch } from "@lib/store";

import { useAvailableTabs } from "../useHaNTabs";

// Typing debounce
const SETTLE_DELAY_MS = 250;

/** The search's query, and the people it opened who aren't in the building today, for their details to show. */
export const useHaNSearchContextValue = () => {
  const dispatch = useAppDispatch();
  const kinds = getHaNSearchKinds(useAvailableTabs());
  const [query, setQueryState] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [searchedPeople, setSearchedPeople] = useState<ReadonlyMap<string, HaNSearchPerson>>(new Map());
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const setQuery = (nextQuery: string) => {
    const trimmedQuery = nextQuery.trim();

    // A new search asks the people search again, instead of keeping the previous search's answers
    if (!query.trim() && trimmedQuery) {
      dispatch(clearPeopleSearch());
    }

    setQueryState(nextQuery);
    clearTimeout(settleTimerRef.current);

    if (!trimmedQuery) {
      setSettledQuery("");

      return;
    }

    settleTimerRef.current = setTimeout(() => {
      startTransition(() => {
        setSettledQuery(trimmedQuery);
      });

      if (searchesHaNPeople(trimmedQuery, kinds)) {
        dispatch(searchPeople(trimmedQuery));
      }
    }, SETTLE_DELAY_MS);
  };

  const rememberPerson = (person: HaNSearchPerson) => {
    setSearchedPeople(previous => new Map(previous).set(person.id, person));
  };

  return {
    query,

    setQuery,

    settledQuery,

    kinds,

    isSearching: query.trim() !== "" && settledQuery !== "",

    searchedPeople,

    rememberPerson,
  };
};

export type HaNSearchContextValue = ReturnType<typeof useHaNSearchContextValue>;

export const HaNSearchContext = createContext<HaNSearchContextValue | null>(null);
